// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductBadges from "@guestsupply/App/Components/Common/ProductBadges";
import AutoDeliveryLink, { AutoDeliveryLinkStyle } from "@guestsupply/App/Components/Product/AutoDeliveryLink";
import ProductActions, { ProductActionsStyle } from "@guestsupply/App/Components/Product/ProductActions";
import ProductGridContainer, {
    ProductGridContainerStyle,
} from "@guestsupply/App/Components/Product/ProductGridContainer";
import xUseWidgetBadges from "@guestsupply/App/Hooks/xUseWidgetBadges";
import { xIsAuthenticated } from "@guestsupply/App/Store/Selectors/GeneralSelectors";
import { getSettingsCollection } from "@guestsupply/App/Store/Selectors/SettingsSelectors";
import { ProductAvailabilityStyles } from "@guestsupply/Overrides/Components/ProductAvailability";
import ProductContextAvailability from "@guestsupply/Overrides/Components/ProductContextAvailability";
import { mergeWithCss, parseBoolean } from "@guestsupply/XDL/Utilities";
import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendProductRecommendationTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendProductRecommendationTracking";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Badge from "@insite/mobius/Badge";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
    productSettings: getSettingsCollection(state).productSettings,
    isAuthenticated: xIsAuthenticated(state),
    cannotProp: getSession(state).properties?.cannotCreateRecurringOrders,
    isPunchout: getSettingsCollection(state).customSettings.isPunchout,
});

const mapDispatchToProps = {
    updateProductInfo,
    sendProductRecommendationTracking,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    recommendationId?: number;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
    extendedStyles?: ProductCarouselProductStyles;
    // Xng-Changes
    imageSize?: string;
    removeFromCompare?: (product: ProductModel) => React.ReactNode;
    showAvailability?: boolean;
    showAutoDelivery?: boolean;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductStyles {
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    addToCartContainer?: GridContainerProps;
    qtyGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    // Xng-Changes
    productGridContainerStyles?: ProductGridContainerStyle;
    productBadgesWrapper?: InjectableCss;
    imageAndDescriptionSection?: InjectableCss;
    productImageBadgesStyles?: ProductPartNumbersStyles;
    productTextBadgesStyles?: ProductPartNumbersStyles;
    productPriceStyles?: ProductPriceStyles;
    productActions?: ProductActionsStyle;
    productAddToListWrapper?: InjectableCss;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    availabilityDeliveryWrapper?: InjectableCss;
    autoDeliveryStyles?: AutoDeliveryLinkStyle;
}

// Xng-Changes: please check ProductCarouselProductExtensions.ts for styles
export const productCarouselProductStyles: ProductCarouselProductStyles = {
    productBrandWrapper: {
        css: css`
            margin: 6px 0;
            min-height: 22px;
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            css: css`
                margin-bottom: 6px;
            `,
        },
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                flex-grow: 0;
                margin-bottom: 6px;
            `,
        },
        erpNumberLabelText: { size: 13 },
        erpNumberValueText: { size: 13 },
    },
    productPriceWrapper: {
        css: css`
            flex-grow: 1;
            margin-bottom: 6px;
        `,
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 6px;
            `,
        },
    },
    addToCartContainer: {
        gap: 10,
        css: css`
            flex-grow: 0;
            margin-bottom: 6px;
        `,
    },
    qtyGridItem: { width: 3 },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 0;
            `,
        },
    },
    addToCartButtonGridItem: { width: 9 },
    productAddToCartButtonStyles: {
        css: css`
            width: 100%;
        `,
    },
    productAddToListLinkStyles: {
        link: {
            typographyProps: { size: 13 },
            css: css`
                margin-bottom: 12px;
            `,
        },
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
};

const ProductCarouselProduct = ({
    carouselId,
    product,
    showImage,
    imageSize,
    showTitle,
    showPartNumbers,
    showPrice,
    showAddToCart,
    showAddToList,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    updateProductInfo,
    extendedStyles,
    productInfo,
    productSettings,
    recommendationId,
    sendProductRecommendationTracking,
    // Xng-Changes
    removeFromCompare,
    showAvailability,
    showAutoDelivery = false,
    cannotProp,
    isAuthenticated,
    isPunchout,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productCarouselProductStyles, extendedStyles));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    const trackProductRecommendation = (isNewPageWasOpen: boolean) => {
        if (recommendationId) {
            sendProductRecommendationTracking({ recommendationId, isNewPageWasOpen });
        }
    };

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        productContext,
        showTextBadge: showTextBadges,
        showImageBadge: showImageBadges,
        styles,
    });

    // xng-change
    // const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });
    const { ootbBadgesAndTags, customBadgesAndTags } = xUseWidgetBadges({
        productContext,
        showTextBadges,
        showImageBadges,
    });

    // Xng-Changes
    const cannotAutoDeliver = () => {
        return cannotProp && parseBoolean(cannotProp) && isPunchout;
    };
    const subscriptionFlag = productContext.product.properties?.subscription_Flag?.toLocaleLowerCase();
    const enableAutoDelivery = isAuthenticated && subscriptionFlag === "true" && !cannotAutoDeliver();
    const extendedProductGridContainerStyles = mergeWithCss(styles.productGridContainerStyles, {
        container: {
            css: css`
                padding: 10px;
                width: 100%;
                max-width: ${parseInt(imageSize || "150", 10) + 250}px;
            `,
        },
        // Xng-Changes
        image: {
            css: css`
                position: relative;
                width: auto;
                height: auto;
                margin: 0 auto;
                border-radius: 4px;
            `,
        },
    });

    return (
        <>
            <ProductContext.Provider value={productContext}>
                <ProductGridContainer extendedStyles={extendedProductGridContainerStyles}>
                    <ProductGridContainer.Info>
                        <StyledWrapper {...styles.imageAndDescriptionSection}>
                            {/* Xng-Changes */}
                            {removeFromCompare?.(product)}
                            {showImage && !showOverlayBadge && (
                                <ProductGridContainer.Image>
                                    <ProductImage
                                        product={productContext}
                                        onClick={() => trackProductRecommendation(true)}
                                        extendedStyles={styles.productImageStyles}
                                        {...{ showPreviouslyPurchasedTag: true }}
                                    />
                                </ProductGridContainer.Image>
                            )}
                            {showImage && showOverlayBadge && overlayBadge && (
                                <ProductGridContainer.Image>
                                    <StyledWrapper
                                        {...styles.badgeAreaWrapper}
                                        ref={badgeAreaWrapperRef}
                                        data-test-selector="productCarouselBadge"
                                    >
                                        <ProductImage
                                            extendedStyles={styles.productImageStyles}
                                            product={productContext}
                                        />
                                        <StyledWrapper
                                            {...overlayBadgeWrapperStyles}
                                            onClick={overlayBadgeWrapperClickHandler}
                                        >
                                            <Badge
                                                type={overlayBadge.badgeType}
                                                value={
                                                    overlayBadge.badgeType === "Image"
                                                        ? overlayBadge.largeImageBadgePath
                                                        : overlayBadge.displayText
                                                }
                                                badgeTextColor={overlayBadge.textColorHexCode}
                                                badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                                badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                                imageAltText={
                                                    overlayBadge.imageAltText
                                                        ? overlayBadge.imageAltText
                                                        : overlayBadge.name
                                                }
                                                textBadgeCss={styles.badgeText}
                                                imageBadgeProps={styles.badgeImageProps}
                                            />
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </ProductGridContainer.Image>
                            )}
                            <StyledWrapper className="product-description">
                                {showTitle && (
                                    <ProductDescription
                                        product={productContext}
                                        onClick={() => trackProductRecommendation(true)}
                                        extendedStyles={styles.productDescriptionStyles}
                                    />
                                )}
                                {showPartNumbers && (
                                    <ProductPartNumbers
                                        productNumber={product.productNumber}
                                        customerProductNumber={product.customerProductNumber}
                                        manufacturerItem={product.manufacturerItem}
                                        showCustomerName={false}
                                        showManufacturerItem={false}
                                        showLabel
                                        extendedStyles={styles.productPartNumbersStyles}
                                    />
                                )}
                                {/* xng-change */}
                                {/* <StyledWrapper {...styles.productBadgesWrapper}>
                                    {showImageBadges && imageBadges && (
                                        <ProductImageBadges
                                            badgeList={imageBadges}
                                            maxBadges={maximumNumberOfImageBadges ?? 4}
                                            extendedStyles={styles.productImageBadgesStyles}
                                        />
                                    )}
                                    {showTextBadges && textBadges && (
                                        <ProductTextBadges
                                            badgeList={textBadges}
                                            maxBadges={maximumNumberOfTextBadges ?? 4}
                                            extendedStyles={styles.productTextBadgesStyles}
                                        />
                                    )}
                                </StyledWrapper> */}
                                <ProductBadges
                                    ootbBadgesAndTags={ootbBadgesAndTags}
                                    customBadgesAndTags={customBadgesAndTags}
                                />
                                {showPrice && (
                                    <StyledWrapper {...styles.productPriceWrapper}>
                                        <VisuallyHidden
                                            as="label"
                                            htmlFor={`${carouselId}-uom`}
                                            id={`${carouselId}-uom-label`}
                                        >
                                            {translate("U/M")}
                                        </VisuallyHidden>
                                        <ProductPrice
                                            product={productContext}
                                            showLabel={false}
                                            showSavings={true}
                                            showPack={false}
                                            showSavingsAmount={productSettings.showSavingsAmount}
                                            showSavingsPercent={productSettings.showSavingsPercent}
                                            extendedStyles={styles.productPriceStyles}
                                        />
                                        {/* XNG-Changes: adding the option to show availability */}
                                        <StyledWrapper {...styles.availabilityDeliveryWrapper}>
                                            {showAvailability && (
                                                <ProductContextAvailability
                                                    extendedStyles={styles.productAvailabilityStyles}
                                                />
                                            )}
                                            {showAutoDelivery && enableAutoDelivery && (
                                                <AutoDeliveryLink extendedStyles={styles.autoDeliveryStyles} />
                                            )}
                                        </StyledWrapper>
                                        {product.unitOfMeasures && (
                                            <ProductUnitOfMeasureSelect
                                                labelOverride=""
                                                extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                                id={`${carouselId}-uom`}
                                            />
                                        )}
                                        <ProductQuantityBreakPricing
                                            extendedStyles={styles.productQuantityBreakPricingStyles}
                                        />
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    </ProductGridContainer.Info>
                    <ProductGridContainer.AddToCartActions>
                        {showAddToCart && <ProductActions showDisabled={true} extendedStyles={styles.productActions} />}
                    </ProductGridContainer.AddToCartActions>
                    <ProductGridContainer.AddToList>
                        <StyledWrapper {...styles.productAddToListWrapper}>
                            {showAddToList && (
                                <ProductAddToListLink extendedStyles={styles.productAddToListLinkStyles} />
                            )}
                        </StyledWrapper>
                    </ProductGridContainer.AddToList>
                </ProductGridContainer>
            </ProductContext.Provider>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProduct);
